import type { ContractorAgreement, ContractorAgreementFields } from '../types/contractorOnboard';
import type { JobOfferSection, JobOfferSignature } from '../types/hiring';
import { assembleContractorAgreementSections, contractorAgreementFullName, formatContractorPay } from './contractorAgreement';
import { LCTF_OFFER_COMPANY, LCTF_OFFER_LEGAL } from './jobOfferLetter';

const CHICAGO = 'America/Chicago';

export function parseContractorAgreementDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'object') {
    const raw = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof raw.toDate === 'function') {
      const parsed = raw.toDate();
      return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
    }
    const seconds = typeof raw.seconds === 'number' ? raw.seconds : raw._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
  }
  return null;
}

export function formatContractorAgreementStamp(value: Date | null | undefined): string {
  if (!value || Number.isNaN(value.getTime())) return '—';
  return `${value.toLocaleString('en-US', {
    timeZone: CHICAGO,
    dateStyle: 'long',
    timeStyle: 'short',
  })} CT`;
}

export function parseAgreementSignature(raw: unknown): JobOfferSignature | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const signedBy = String(s.signedBy || '');
  const dataUrl = typeof s.dataUrl === 'string' ? s.dataUrl : '';
  if (!signedBy && !dataUrl) return null;
  return {
    dataUrl,
    signedBy,
    signedAt: parseContractorAgreementDate(s.signedAt) || new Date(),
    title: typeof s.title === 'string' ? s.title : undefined,
    ipAddress: typeof s.ipAddress === 'string' && s.ipAddress.trim() ? s.ipAddress.trim() : undefined,
    userAgent: typeof s.userAgent === 'string' && s.userAgent.trim() ? s.userAgent.trim() : undefined,
    source: typeof s.source === 'string' && s.source.trim() ? s.source.trim() : undefined,
    grandfathered: s.grandfathered === true,
  };
}

function parseAgreementFields(raw: unknown): ContractorAgreementFields {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    contractorFirstName: String(r.contractorFirstName || ''),
    contractorLastName: String(r.contractorLastName || ''),
    contractorEmail: String(r.contractorEmail || ''),
    jobTitle: String(r.jobTitle || ''),
    scope: String(r.scope || ''),
    payAmount: typeof r.payAmount === 'number' ? r.payAmount : Number(r.payAmount) || 0,
    payUnit: r.payUnit === 'day' ? 'day' : 'hour',
    location: String(r.location || ''),
    ceoName: String(r.ceoName || ''),
    ceoTitle: String(r.ceoTitle || ''),
  };
}

function isLetterheadSection(section: JobOfferSection): boolean {
  return section.id === 'letterhead' || section.heading === LCTF_OFFER_COMPANY;
}

export function contractorAgreementBodySections(agreement: Pick<ContractorAgreement, 'sections' | 'fields'>): JobOfferSection[] {
  const sections = agreement.sections?.length
    ? agreement.sections
    : assembleContractorAgreementSections(agreement.fields);
  return sections.filter(section => !isLetterheadSection(section));
}

export type ContractorAgreementPdfSignBlock = {
  heading: string;
  name: string;
  title: string;
  stamped: string;
  dataUrl: string;
  ipAddress: string;
  userAgent: string;
  missingInk: boolean;
};

export type ContractorAgreementPdfMeta = {
  company: string;
  legal: string;
  title: string;
  contractorName: string;
  email: string;
  jobTitle: string;
  location: string;
  pay: string;
  facts: Array<{ label: string; value: string }>;
  sections: JobOfferSection[];
  companySign: ContractorAgreementPdfSignBlock;
  contractorSign: ContractorAgreementPdfSignBlock;
  audit: Array<{ label: string; value: string }>;
  bindingNote: string;
};

function signBlock(
  heading: string,
  fallbackName: string,
  fallbackTitle: string,
  signature: JobOfferSignature | null | undefined,
): ContractorAgreementPdfSignBlock {
  const missingInk = !signature?.dataUrl;
  const stamped = signature?.grandfathered && missingInk
    ? 'Staff attested (grandfathered)'
    : formatContractorAgreementStamp(signature?.signedAt);
  return {
    heading,
    name: signature?.signedBy || fallbackName,
    title: signature?.title || fallbackTitle,
    stamped,
    dataUrl: signature?.dataUrl || '',
    ipAddress: signature?.ipAddress || '—',
    userAgent: signature?.userAgent || '—',
    missingInk,
  };
}

export function contractorAgreementPdfMeta(agreement: ContractorAgreement): ContractorAgreementPdfMeta {
  const contractorName = contractorAgreementFullName(agreement.fields) || 'Contractor';
  const companySign = signBlock(
    LCTF_OFFER_COMPANY,
    agreement.fields.ceoName || 'Matt Lynch',
    agreement.fields.ceoTitle || 'CEO',
    agreement.ceoSignature,
  );
  const contractorSign = signBlock(
    'Contractor',
    contractorName,
    'Independent contractor',
    agreement.contractorSignature,
  );
  return {
    company: LCTF_OFFER_COMPANY,
    legal: LCTF_OFFER_LEGAL,
    title: 'Independent Contractor Agreement',
    contractorName,
    email: agreement.fields.contractorEmail || '',
    jobTitle: agreement.fields.jobTitle || '',
    location: agreement.fields.location || '',
    pay: formatContractorPay(agreement.fields),
    facts: [
      { label: 'Contractor', value: contractorName },
      { label: 'Email', value: agreement.fields.contractorEmail || '—' },
      { label: 'Work / raise', value: agreement.fields.jobTitle || '—' },
      { label: 'Location', value: agreement.fields.location || '—' },
      { label: 'Pay', value: formatContractorPay(agreement.fields) },
    ],
    sections: contractorAgreementBodySections(agreement),
    companySign,
    contractorSign,
    audit: [
      { label: 'Agreement ID', value: agreement.id || '—' },
      { label: 'Onboarding slug', value: agreement.slug || '—' },
      { label: 'Record status', value: agreement.status || '—' },
      { label: 'Company signed', value: companySign.stamped },
      { label: 'Company IP', value: companySign.ipAddress },
      { label: 'Company device', value: companySign.userAgent },
      { label: 'Contractor signed', value: contractorSign.stamped },
      { label: 'Contractor IP', value: contractorSign.ipAddress },
      { label: 'Contractor device', value: contractorSign.userAgent },
      { label: 'PDF generated', value: formatContractorAgreementStamp(new Date()) },
    ],
    bindingNote:
      'By signing, both parties acknowledge this electronic record is the official Independent Contractor Agreement. Ink signatures, timestamps, and device details above are stored with the agreement in the Leiper\'s Creek Timber Frames CRM.',
  };
}

export function contractorAgreementFromPublicPayload(payload: {
  id: string;
  slug?: string;
  status?: string;
  fields?: Record<string, unknown>;
  sections?: JobOfferSection[];
  ceoSignature?: unknown;
  contractorSignature?: unknown;
  signedAt?: unknown;
  signedPdfUrl?: string | null;
  signedPdfPath?: string | null;
  applicantId?: string;
}): ContractorAgreement {
  const fields = parseAgreementFields(payload.fields);
  const signedAt = parseContractorAgreementDate(payload.signedAt);
  return {
    id: payload.id,
    applicantId: payload.applicantId || '',
    slug: payload.slug || '',
    status: (payload.status as ContractorAgreement['status']) || 'signed',
    fields,
    sections: Array.isArray(payload.sections) ? payload.sections : [],
    ceoSignature: parseAgreementSignature(payload.ceoSignature),
    contractorSignature: parseAgreementSignature(payload.contractorSignature),
    signedAt,
    signedPdfUrl: payload.signedPdfUrl || null,
    signedPdfPath: payload.signedPdfPath || null,
    createdBy: '',
    createdAt: signedAt || new Date(),
    updatedAt: signedAt || new Date(),
  };
}
